<template>
  <v-row align="center">
    <v-col cols="12" md="4" v-if="allows('dayOfMonth')">
      <v-text-field
        type="number"
        min="1"
        max="31"
        :label="dayLabel"
        :disabled="!isDayOfMonth"
        :value="isDayOfMonth ? rawValue.dayOfMonth : null"
        @input="onDayInput"
      />
    </v-col>
    <v-col cols="12" md="8">
      <v-checkbox
        v-for="option in checkboxOptions"
        :key="option.value"
        :label="option.text"
        :input-value="effectiveDayType === option.value"
        :disabled="effectiveDayType !== option.value && effectiveDayType !== 'dayOfMonth' "
        hide-details
        class="mt-0"
        @change="onCheck(option.value, $event)"
      />
    </v-col>
    <!-- Solo aplica a los tipos que pueden caer en fin de semana: los de dia
         habil ya son de lunes a viernes. -->
    <v-col cols="12" v-if="canFallOnWeekend">
      <v-checkbox
        label="Sólo de lunes a viernes (si cae en fin de semana, sale el lunes)"
        :input-value="weekdaysOnly"
        hide-details
        class="mt-0"
        @change="onWeekdaysOnly($event)"
      />
    </v-col>
  </v-row>
</template>

<script>
const ALL_OPTIONS = [
  { value: "firstBusinessDay", text: "Primer día hábil del mes" },
  { value: "lastDay", text: "Último día del mes" },
  { value: "lastBusinessDay", text: "Último día hábil del mes" }
];

const isValidDayOfMonth = day => typeof day === "number" && day >= 1 && day <= 31;

export default {
  name: "schedule-selector",
  props: {
    // Sin default propio: un default fijo no puede mirar `allowed` (Vue 2 no
    // se lo pasa a la funcion de default de otro prop), asi que la
    // normalizacion completa -incluido el caso "no vino nada"- vive en las
    // computadas y en created(), que si conocen `allowed`.
    value: { type: Object },
    allowed: {
      type: Array,
      default: () => ["dayOfMonth", "lastDay", "lastBusinessDay", "firstBusinessDay"]
    },
    dayLabel: { type: String, default: "Día del mes" }
  },
  computed: {
    // Nunca null/undefined, para que ninguna computada de abajo reviente con
    // `Cannot read property 'dayType' of null/undefined`.
    rawValue() {
      return this.value && typeof this.value === "object" ? this.value : {};
    },
    // El primer valor de `allowed` que tiene sentido como default: dayOfMonth
    // si esta permitido (comportamiento historico), si no la primera opcion
    // que el consumidor haya habilitado.
    defaultDayType() {
      return this.allowed.indexOf("dayOfMonth") !== -1 ? "dayOfMonth" : this.allowed[0];
    },
    // El dayType que se muestra y se usa para decidir que deshabilitar. Si el
    // que trae rawValue no esta en `allowed` (o no vino ninguno), cae a
    // defaultDayType en vez de dejar el control entero deshabilitado.
    effectiveDayType() {
      const dayType = this.rawValue.dayType;
      return dayType && this.allowed.indexOf(dayType) !== -1 ? dayType : this.defaultDayType;
    },
    isDayOfMonth() {
      return this.effectiveDayType === "dayOfMonth";
    },
    canFallOnWeekend() {
      return this.effectiveDayType === "dayOfMonth" || this.effectiveDayType === "lastDay";
    },
    // Encendido por defecto: solo apagado si vino false explicito.
    weekdaysOnly() {
      return this.rawValue.weekdaysOnly !== false;
    },
    checkboxOptions() {
      return ALL_OPTIONS.filter(o => this.allowed.indexOf(o.value) !== -1);
    }
  },
  created() {
    // Se corrige UNA sola vez, al recibir el valor inicial -no con un watch
    // continuo- para no pelearle el campo numerico al usuario en cada tecla:
    // mientras tipea, dayOfMonth pasa por null de forma transitoria y eso no
    // es un valor "recibido de afuera" que haya que corregir. Lo que si se
    // corrige aca es exactamente lo que un consumidor externo puede mandar
    // roto: undefined, null, {}, un dayType que no esta en `allowed`, o
    // dayType:"dayOfMonth" sin un numero valido (el caso que el backend ya
    // trata como "sin configurar").
    if (this.needsNormalization()) {
      this.$emit("input", this.buildNormalizedValue());
    }
  },
  methods: {
    allows(dayType) {
      return this.allowed.indexOf(dayType) !== -1;
    },
    needsNormalization() {
      const dayType = this.rawValue.dayType;
      if (!dayType || this.allowed.indexOf(dayType) === -1) return true;
      if (dayType === "dayOfMonth" && !isValidDayOfMonth(this.rawValue.dayOfMonth)) return true;
      return false;
    },
    buildNormalizedValue() {
      if (!this.isDayOfMonth) {
        return { dayType: this.effectiveDayType, dayOfMonth: null, weekdaysOnly: this.weekdaysOnly };
      }
      return {
        dayType: "dayOfMonth",
        dayOfMonth: isValidDayOfMonth(this.rawValue.dayOfMonth) ? this.rawValue.dayOfMonth : 1,
        weekdaysOnly: this.weekdaysOnly
      };
    },
    onDayInput(raw) {
      const day = Number(raw);
      this.$emit("input", {
        dayType: "dayOfMonth",
        dayOfMonth: day >= 1 && day <= 31 ? day : null,
        weekdaysOnly: this.weekdaysOnly
      });
    },
    onWeekdaysOnly(checked) {
      this.$emit("input", Object.assign({}, this.buildNormalizedValue(), { weekdaysOnly: !!checked }));
    },
    // Marcar una opcion desmarca las otras y deshabilita el numero. Desmarcarla
    // vuelve al default (dayOfMonth si esta permitido; si no, la primera
    // opcion de `allowed`) con un valor siempre valido -nunca deja
    // dayType:"dayOfMonth" sin numero, ni un dayType afuera de `allowed`.
    onCheck(dayType, checked) {
      if (checked) {
        this.$emit("input", { dayType, dayOfMonth: null, weekdaysOnly: this.weekdaysOnly });
      } else if (this.defaultDayType === "dayOfMonth") {
        this.$emit("input", { dayType: "dayOfMonth", dayOfMonth: 1, weekdaysOnly: this.weekdaysOnly });
      } else {
        this.$emit("input", { dayType: this.defaultDayType, dayOfMonth: null, weekdaysOnly: this.weekdaysOnly });
      }
    }
  }
};
</script>
